import { PayloadAction } from '@reduxjs/toolkit'

import {
  REFERRAL_IMPORT_DIRECTION,
  REFERRALS_IMPORT_STEP,
} from '@/lib/constants'
import { isValidEmail } from '@/lib/helpers/emailAddressHelpers'
import { HydraMember } from '@/types/api'
import { FormErrors, ModifierKey } from '@/types/misc'
import { BulkImportReferralCollectionItem } from '@/types/responses/referral-import'

export interface ReferralsImportState {
  isOpen: boolean
  showImported: boolean
  step: ReferralsImportStep
  initialStep: ReferralsImportStep
  onClientDuplicate: ReferralsImportOnClientDuplicate
  hasInvites: boolean
  hasSelectedMultiple: boolean
  hasUpdatableClients: boolean
  canBulkUpdateSelectedOnly: boolean
  canPauseClientEmails: boolean
  selectedCount: number
  prevSelectedItemIndex: number
  importItems: ReferralsImportItem[]
}

export type ReferralsImportStep = 'upload' | 'resolveImport'
export type ReferralsImportOnClientDuplicate = 'keep-original' | 'update'

export type ReferralsImportItem =
  HydraMember<BulkImportReferralCollectionItem> & {
    refUUID: string
    onClientDuplicate: ReferralsImportOnClientDuplicate
    importStatus: 'invalid' | 'ready' | 'processing' | 'imported' | 'error'
    importErrors?: FormErrors
    importingCompanyValid: boolean
    isFromInvite: boolean
    isToInvite: boolean
    newServiceAreaId: number | null
    hasPausedClientEmails: boolean
    multipleFromInvites: boolean
    multipleToInvites: boolean
    bulkFromAllCount: number
    bulkToAllCount: number
    bulkFromCompanyCount: number
    bulkToCompanyCount: number
    selected: boolean
  }

export const initialReferralsImportItemState: Pick<
  ReferralsImportItem,
  | 'importStatus'
  | 'importingCompanyValid'
  | 'isFromInvite'
  | 'isToInvite'
  | 'newServiceAreaId'
  | 'hasPausedClientEmails'
  | 'multipleFromInvites'
  | 'multipleToInvites'
  | 'bulkFromAllCount'
  | 'bulkToAllCount'
  | 'bulkFromCompanyCount'
  | 'bulkToCompanyCount'
  | 'selected'
> = {
  importStatus: 'ready',
  importingCompanyValid: false,
  isFromInvite: false,
  isToInvite: false,
  newServiceAreaId: null,
  hasPausedClientEmails: false,
  multipleFromInvites: false,
  multipleToInvites: false,
  bulkFromAllCount: 0,
  bulkToAllCount: 0,
  bulkFromCompanyCount: 0,
  bulkToCompanyCount: 0,
  selected: false,
}

export type ReferralsImportItemPartial = {
  refUUID: string
} & Partial<ReferralsImportItem>

export const initialReferralsImportState: ReferralsImportState = {
  isOpen: false,
  showImported: false,
  step: REFERRALS_IMPORT_STEP.upload,
  initialStep: REFERRALS_IMPORT_STEP.upload,
  onClientDuplicate: 'keep-original',
  hasInvites: false,
  hasSelectedMultiple: false,
  hasUpdatableClients: false,
  canBulkUpdateSelectedOnly: false,
  canPauseClientEmails: false,
  selectedCount: 0,
  prevSelectedItemIndex: -1,
  importItems: [],
}

// Reducers
export const setIsOpenReducer = (
  state: ReferralsImportState,
  action: PayloadAction<boolean>,
) => {
  state.isOpen = action.payload
}

export const setShowImportedReducer = (
  state: ReferralsImportState,
  action: PayloadAction<boolean>,
) => {
  state.showImported = action.payload
}

export const setStepReducer = (
  state: ReferralsImportState,
  action: PayloadAction<ReferralsImportState['step']>,
) => {
  state.step = action.payload
}

export const setOnClientDuplicateReducer = (
  state: ReferralsImportState,
  action: PayloadAction<ReferralsImportState['onClientDuplicate']>,
) => {
  state.onClientDuplicate = action.payload
}

export const setImportItemsReducer = (
  state: ReferralsImportState,
  action: PayloadAction<ReferralsImportState['importItems']>,
) => {
  state.importItems = action.payload
  state.importItems.forEach((item) => {
    updateItemConstraints(item)
    validate(item)
  })
  updateState(state)
}

export const updateImportItemReducer = (
  state: ReferralsImportState,
  action: PayloadAction<ReferralsImportItemPartial>,
) => {
  const item = state.importItems.find(
    (i) => i.refUUID === action.payload.refUUID,
  )
  if (item) {
    Object.assign(item, action.payload)
    updateItemConstraints(item)
    validate(item)
  }
  updateState(state)
}

export const updateImportItemStatusReducer = (
  state: ReferralsImportState,
  action: PayloadAction<
    Pick<ReferralsImportItem, 'refUUID' | 'importStatus' | 'importErrors'>
  >,
) => {
  const item = state.importItems.find(
    (i) => i.refUUID === action.payload.refUUID,
  )
  if (item) {
    item.importStatus = action.payload.importStatus
    item.importErrors = action.payload.importErrors
  }
  updateState(state)
}

export const selectItemsReducer = (
  state: ReferralsImportState,
  action: PayloadAction<{
    refUUID: string
    modifier: null | ModifierKey
  }>,
) => {
  if (action.payload.modifier === 'shiftKey') {
    let currentlySelectedItemIndex = -1
    state.importItems.forEach((item, index) => {
      if (item.refUUID === action.payload.refUUID) {
        currentlySelectedItemIndex = index
      }
    })
    const [min, max] =
      state.prevSelectedItemIndex < currentlySelectedItemIndex
        ? [state.prevSelectedItemIndex, currentlySelectedItemIndex]
        : [currentlySelectedItemIndex, state.prevSelectedItemIndex]
    state.importItems.forEach((item, index) => {
      item.selected = item.selected || (index >= min && index <= max)
    })
    state.prevSelectedItemIndex = currentlySelectedItemIndex
  } else {
    state.importItems.forEach((item, index) => {
      if (item.refUUID === action.payload.refUUID) {
        item.selected = !item.selected
        state.prevSelectedItemIndex = index
      }
    })
  }
  updateSelectedState(state)
}

export type BulkUpdatePayload = {
  filter: {
    selected?: boolean
    fromCompany?: number
    toCompany?: number
  }
  data: Partial<
    Pick<
      ReferralsImportItem,
      | 'circumstances'
      | 'inviteFromAdviser'
      | 'inviteToAdviser'
      | 'onClientDuplicate'
      | 'fromCompanyUser'
      | 'fromCompanyUserDefaultUsed'
      | 'toCompanyUser'
      | 'toCompanyUserDefaultUsed'
      | 'hasPausedClientEmails'
      | 'referralDate'
      | 'selected'
      | 'newServiceAreaId'
      | 'serviceArea'
      | 'serviceAreaDefaultUsed'
      | 'status'
    >
  >
}

export const bulkUpdateImportItemsReducer = (
  state: ReferralsImportState,
  action: PayloadAction<BulkUpdatePayload>,
) => {
  let anyChanged = false
  state.importItems.forEach((item) => {
    if (item.importStatus != 'imported') {
      let assign = true
      if (action.payload.filter?.selected === true) {
        assign = assign && item.selected
      }
      if (action.payload.filter?.fromCompany) {
        assign =
          assign && item.fromCompany?.id === action.payload.filter.fromCompany
      }
      if (action.payload.filter?.toCompany) {
        assign =
          assign && item.toCompany?.id === action.payload.filter.toCompany
      }
      if (assign) {
        Object.assign(item, action.payload.data)
        anyChanged = true
        updateItemConstraints(item)
        validate(item)
      }
    }
  })
  if (anyChanged) {
    updateState(state)
  }
}

export const removeImportItemReducer = (
  state: ReferralsImportState,
  action: PayloadAction<string>,
) => {
  state.importItems = state.importItems.filter(
    (i) => i.refUUID !== action.payload,
  )
  if (state.importItems.length === 0) {
    state.isOpen = false
    state.step = state.initialStep
  }
  updateState(state)
}

function updateItemConstraints(item: ReferralsImportItem) {
  // detect invite
  item.isFromInvite =
    !item.fromCompany || (!item.fromCompanyUser && item.fromAdviserEmail != '')
  item.isToInvite =
    !item.toCompany || (!item.toCompanyUser && item.toAdviserEmail != '')
}

function updateState(state: ReferralsImportState) {
  state.hasInvites = false
  state.hasUpdatableClients = false
  state.canPauseClientEmails = false
  const advisorEmailMap = new Map<string, number>()
  const fromCompaniesMap = new Map<number, number>()
  const toCompaniesMap = new Map<number, number>()
  let bulkAllToCount = 0
  let bulkAllFromCount = 0
  let selectedCount = 0
  // first run, set general state flags and collect maps
  state.importItems.forEach((item) => {
    // - is selected
    if (item.selected) {
      selectedCount++
    }
    // - has invites
    if (!item.toCompany || !item.fromCompany) {
      state.hasInvites = true
    }
    // - has updatable clients
    if (item.importingCompanyClient) {
      state.hasUpdatableClients = true
    }
    // - can pause client emails
    if (item.canPauseClientEmails) {
      state.canPauseClientEmails = true
    }
    // - multiple invites
    if (item.toAdviserEmail) {
      advisorEmailMap.set(
        item.toAdviserEmail.toLowerCase(),
        (advisorEmailMap.get(item.toAdviserEmail.toLowerCase()) || 0) + 1,
      )
    }
    if (item.fromAdviserEmail) {
      advisorEmailMap.set(
        item.fromAdviserEmail.toLowerCase(),
        (advisorEmailMap.get(item.fromAdviserEmail.toLowerCase()) || 0) + 1,
      )
    }
    // - reset counts
    item.bulkFromAllCount = 0
    item.bulkFromCompanyCount = 0
    item.bulkToAllCount = 0
    item.bulkToCompanyCount = 0
    // - bulk counts
    if (item.importStatus === 'ready' || item.importStatus === 'error') {
      if (item.fromCompany) {
        fromCompaniesMap.set(
          item.fromCompany.id,
          (fromCompaniesMap.get(item.fromCompany.id) || 0) + 1,
        )
        bulkAllFromCount++
      }
      if (item.toCompany) {
        toCompaniesMap.set(
          item.toCompany.id,
          (toCompaniesMap.get(item.toCompany.id) || 0) + 1,
        )
        bulkAllToCount++
      }
    }
  })
  // second run, apply maps
  state.importItems.forEach((item) => {
    // - multiple invites
    item.multipleFromInvites =
      advisorEmailMap.get(item.fromAdviserEmail.toLowerCase())! > 1
    item.multipleToInvites =
      advisorEmailMap.get(item.toAdviserEmail.toLowerCase())! > 1
    // - bulk counts
    if (item.importStatus === 'ready' || item.importStatus === 'error') {
      if (item.fromCompany) {
        item.bulkFromCompanyCount =
          fromCompaniesMap.get(item.fromCompany.id) || 0
        item.bulkFromAllCount = bulkAllFromCount
      }
      if (item.toCompany) {
        item.bulkToCompanyCount = toCompaniesMap.get(item.toCompany.id) || 0
        item.bulkToAllCount = bulkAllToCount
      }
    }
  })
  // apply general state flags
  updateSelectedState(state, selectedCount)
}

function updateSelectedState(
  state: ReferralsImportState,
  selectedCount?: number,
) {
  if (selectedCount === undefined) {
    selectedCount = state.importItems.filter((item) => item.selected).length
  }
  state.hasSelectedMultiple = selectedCount > 1
  state.canBulkUpdateSelectedOnly = selectedCount > 0
  state.selectedCount = selectedCount
}

function validate(item: ReferralsImportItem) {
  let isValid = true

  if (item.isFromInvite) {
    // Validate adviser email for invitations purposes
    item.fromAdviserEmailValid = isValidEmail(item.fromAdviserEmail)
  } else {
    item.fromAdviserEmailValid = true
  }

  if (item.isToInvite) {
    // Validate adviser email for invitations purposes
    item.toAdviserEmailValid = isValidEmail(item.toAdviserEmail)
  } else {
    item.toAdviserEmailValid = true
  }

  if (!item.importingCompanyClient) {
    // Validate email for new client purposes
    item.sourceClientEmailValid = isValidEmail(item.sourceClientEmail)
  } else {
    item.sourceClientEmailValid = true
  }

  // Validate importing company
  item.importingCompanyValid = true
  if (!item.importingCompany) {
    item.importingCompanyValid = false
  } else if (item.isFromInvite && item.isToInvite) {
    // both cannot be invites at the same time
    item.importingCompanyValid = false
  } else if (
    item.isFromInvite &&
    item.importingSource === REFERRAL_IMPORT_DIRECTION.FROM
  ) {
    // cannot be an invite and source (case for not existing advisor)
    item.importingCompanyValid = false
  } else if (
    item.isToInvite &&
    item.importingSource === REFERRAL_IMPORT_DIRECTION.TO
  ) {
    // cannot be an invite and source (case for not existing advisor)
    item.importingCompanyValid = false
  }

  // General validation
  isValid =
    item.importingCompanyValid &&
    item.fromAdviserEmailValid &&
    item.toAdviserEmailValid &&
    item.sourceClientEmailValid

  if (!item.canPauseClientEmails) {
    item.hasPausedClientEmails = false
  }

  if (!isValid && item.importStatus !== 'invalid') {
    item.importStatus = 'invalid'
  } else if (isValid && item.importStatus === 'invalid') {
    item.importStatus = 'ready'
  }
}

export const reducers = {
  setIsOpen: setIsOpenReducer,
  setShowImported: setShowImportedReducer,
  setStep: setStepReducer,
  setOnClientDuplicate: setOnClientDuplicateReducer,
  setImportItems: setImportItemsReducer,
  updateImportItem: updateImportItemReducer,
  updateImportItemStatus: updateImportItemStatusReducer,
  selectItems: selectItemsReducer,
  bulkUpdateImportItems: bulkUpdateImportItemsReducer,
  removeImportItem: removeImportItemReducer,
}
